import { Card, CardActions, CardContent, CardHeader, Stack, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { Box, SxProps } from '@mui/system';
import React, { ReactNode, useMemo } from 'react';
import { TypographyVariant } from '@mui/material/styles/createTypography';
import { FormGrid } from '../form-grid/form-grid.component';
import { useBreakpoint } from '../../hooks';

interface Props {
    children?: ReactNode;
    footer?: React.ReactNode;
    icon?: React.ReactNode;
    title?: React.ReactNode;
    hasTable?: boolean;
    sx?: SxProps<Theme>;
    onClick?: () => void;
    actions?: ReactNode | ReactNode[];
    variant?: TypographyVariant;
}

export const Widget: React.FC<Props> = ({
    footer,
    icon,
    title,
    hasTable,
    sx = {},
    children,
    onClick,
    actions,
    variant = 'subtitle1',
}) => {
    const mappedActions = useMemo(() => (Array.isArray(actions) ? actions : [actions]), [actions]);
    const lg = useBreakpoint('lg');

    return (
        <Card sx={{ maxWidth: lg ? 'calc(100vw - 275px)' : '100vw', ...sx }} onClick={onClick} id="widget">
            {title && (
                <CardHeader
                    title={
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1}
                            width="100%"
                            minHeight="35px"
                        >
                            <Stack direction="row" alignItems="center" spacing={2}>
                                {icon}
                                <Typography fontWeight="bold" minWidth="fit-content" variant={variant}>
                                    {title}
                                </Typography>
                            </Stack>

                            {mappedActions && (
                                <FormGrid containerProps={{ spacing: 1, justifyContent: 'flex-end' }}>
                                    {mappedActions.map((action, index) => (
                                        <Box key={index}>{action}</Box>
                                    ))}
                                </FormGrid>
                            )}
                        </Stack>
                    }
                />
            )}
            <CardContent
                sx={
                    hasTable
                        ? {
                              p: 0,
                              '&:last-child': { pb: 0 },
                              '& .MuiDataGrid-root': {
                                  borderRadius: 0,
                                  border: 0,
                              },
                              '& .MuiDataGrid-row:last-child .MuiDataGrid-cell': {
                                  borderBottom: 0,
                              },
                              '& .MuiDataGrid-footerContainer': {
                                  borderTop: 1,
                                  borderColor: 'lightGrey',
                              },
                              '.MuiDataGrid-columnHeaders': {
                                  fontSize: 'smaller',
                              },
                          }
                        : {
                              p: 2,
                          }
                }
            >
                {children}
            </CardContent>
            {footer && <CardActions>{footer}</CardActions>}
        </Card>
    );
};
